import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, School, MapPin, User, Mail, CreditCard, ArrowLeft } from "lucide-react";
import { api, formatCOP } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

let wompiPromise = null;
const loadWompiWidget = () => {
  if (window.WidgetCheckout) return Promise.resolve(window.WidgetCheckout);
  if (wompiPromise) return wompiPromise;
  wompiPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.wompi.co/widget.js";
    s.async = true;
    s.onload = () => resolve(window.WidgetCheckout);
    s.onerror = () => { wompiPromise = null; reject(new Error("widget load failed")); };
    document.body.appendChild(s);
  });
  return wompiPromise;
};

export default function CheckoutDialog({ open, onOpenChange, directItems = null, shippingFee = 0, onBack }) {
  const { items: cartItems, total: cartTotal } = useCart();
  const items = directItems && directItems.length ? directItems : cartItems;
  const total = directItems && directItems.length
    ? directItems.reduce((s, i) => s + i.price * i.quantity, 0)
    : cartTotal;
  const [cities, setCities] = useState([]);
  const [deliveryType, setDeliveryType] = useState("colegio");
  const [form, setForm] = useState({ name: "", email: "", school: "", address: "", city: "", student: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/cities").then((r) => {
        setCities(r.data.cities || []);
        setDeliveryType(r.data.delivery_type || "colegio");
      }).catch(() => setCities([]));
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isHome = deliveryType === "domicilio";

  const valid =
    form.name.trim() &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.city &&
    (isHome ? form.address.trim() : form.school.trim() && form.student.trim());

  const handlePay = async () => {
    if (!valid) {
      toast.error("Completa todos los datos de entrega.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/create", {
        items: items.map((i) => ({ book_id: i.book_id, quantity: i.quantity })),
        customer: { name: form.name.trim(), email: form.email.trim() },
        delivery: isHome
          ? { address: form.address.trim(), city: form.city }
          : { school: form.school.trim(), city: form.city, student: form.student.trim() },
        origin_url: window.location.origin,
      });

      const WidgetCheckout = await loadWompiWidget();
      const checkout = new WidgetCheckout({
        currency: data.currency,
        amountInCents: data.amount_in_cents,
        reference: data.reference,
        publicKey: data.public_key,
        signature: { integrity: data.integrity },
        redirectUrl: data.redirect_url,
        customerData: { email: form.email.trim(), fullName: form.name.trim() },
      });

      // Close the checkout dialog first so Radix releases the body scroll/pointer
      // lock, then open the Wompi widget on the next frame.
      onOpenChange(false);
      setLoading(false);
      requestAnimationFrame(() => {
        setTimeout(() => {
          checkout.open((result) => {
            const txId = result?.transaction?.id || "";
            window.location.href = `/pago/exito?reference=${encodeURIComponent(data.reference)}&transaction_id=${encodeURIComponent(txId)}`;
          });
        }, 100);
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo iniciar el pago.");
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] focus:ring-4 focus:ring-[var(--lc-primary)]/20 outline-none transition-colors p-3.5 bg-[var(--lc-bg)] text-[var(--lc-ink)]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[92vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)] flex items-center gap-2">
            {onBack && (
              <button
                onClick={onBack}
                data-testid="checkout-back-button"
                aria-label="Volver"
                className="w-8 h-8 grid place-items-center rounded-full border-2 border-[var(--lc-border)] text-[var(--lc-ink)] hover:border-[var(--lc-primary)] hover:text-[var(--lc-primary)] transition-colors shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            Datos para la entrega
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[var(--lc-slate)] -mt-1">
          {isHome
            ? "Coordinamos la entrega directamente en tu dirección."
            : "Coordinamos la entrega directamente en el colegio del estudiante."}
        </p>

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
              <User className="w-4 h-4 text-[var(--lc-primary)]" /> {isHome ? "Nombre del destinatario" : "Nombre del acudiente"}
            </label>
            <input
              className={field}
              data-testid="checkout-name"
              placeholder="Ej: María González"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
              <Mail className="w-4 h-4 text-[var(--lc-primary)]" /> Correo electrónico
            </label>
            <input
              className={field}
              type="email"
              data-testid="checkout-email"
              placeholder="correo@ejemplo.com"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>

          {isHome ? (
            <div>
              <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
                <MapPin className="w-4 h-4 text-[var(--lc-primary)]" /> Dirección de entrega
              </label>
              <input
                className={field}
                data-testid="checkout-address"
                placeholder="Ej: Calle 123 #45-67, Apto 801"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
                <School className="w-4 h-4 text-[var(--lc-primary)]" /> Nombre del colegio
              </label>
              <input
                className={field}
                data-testid="checkout-school"
                placeholder="Ej: Colegio San José"
                value={form.school}
                onChange={(e) => update("school", e.target.value)}
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
                <MapPin className="w-4 h-4 text-[var(--lc-primary)]" /> Ciudad
              </label>
              <Select value={form.city} onValueChange={(v) => update("city", v)}>
                <SelectTrigger className="p-3.5 h-auto rounded-xl border-2 border-[var(--lc-border)] bg-[var(--lc-bg)]" data-testid="checkout-city">
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`city-option-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isHome && (
              <div>
                <label className="text-sm font-bold text-[var(--lc-ink)] flex items-center gap-1.5 mb-1">
                  <User className="w-4 h-4 text-[var(--lc-primary)]" /> Estudiante
                </label>
                <input
                  className={field}
                  data-testid="checkout-student"
                  placeholder="Nombre del estudiante"
                  value={form.student}
                  onChange={(e) => update("student", e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-[#F0F9FF] p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--lc-slate)] font-semibold">Subtotal</span>
            <span className="font-semibold text-[var(--lc-ink)]" data-testid="checkout-subtotal">{formatCOP(total)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--lc-slate)] font-semibold">Envío</span>
            <span className="font-semibold text-[var(--lc-ink)]" data-testid="checkout-shipping">{formatCOP(shippingFee)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[var(--lc-border)]">
            <span className="font-semibold text-[var(--lc-slate)]">Total a pagar</span>
            <span className="font-display font-black text-2xl text-[var(--lc-ink)]" data-testid="checkout-total">
              {formatCOP(total + shippingFee)}
            </span>
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={loading}
          data-testid="pay-button"
          className="mt-2 w-full flex items-center justify-center gap-2 rounded-full bg-[var(--lc-primary)] text-white py-4 font-display font-extrabold text-lg transition-transform duration-200 hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <CreditCard className="w-5 h-5" />
          )}
          {loading ? "Procesando…" : "Pagar con tarjeta o PSE"}
        </button>
        <p className="text-center text-xs text-[var(--lc-slate)]">
          Pago seguro procesado por Wompi · Tarjeta de crédito, débito y PSE.
        </p>
      </DialogContent>
    </Dialog>
  );
}
